import {useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import {PageTitle} from '../../../_metronic/layout/core';
import Level3Title from '../../../app/common/components/title/Level3Title';
import CustomModal from '../../../app/common/components/modal/CustomModal';

interface InfoItemProps {
  label: string;
  value: React.ReactNode;
  colClass?: string;
}

interface ContactFormValues {
  type: string;
  societe: string;
  nom: string;
  prenom: string;
  email: string;
  telIndicatif: string;
  telNumero: string;
  adresse: string;
  codePostal: string;
  ville: string;
  pays: string;
}

const INITIAL_CONTACT: ContactFormValues = {
  type: 'Personne Morale',
  societe: 'Ediware',
  nom: '',
  prenom: '',
  email: 'hostmaster@magic.fr',
  telIndicatif: '33',
  telNumero: '175437705',
  adresse: '46B avenue du Maine',
  codePostal: '75015',
  ville: 'PARIS',
  pays: 'FR',
};

const InfoItem = ({label, value, colClass = 'col-md-4'}: InfoItemProps): JSX.Element => (
  <div className={`${colClass} mb-6`}>
    <div className="text-muted fs-7 mb-1">{label}</div>
    <div className="fw-semibold fs-5 text-gray-800">{value}</div>
  </div>
);

const DomaineWhoisPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const domaineName = searchParams.get('domaine') ?? 'exemple.fr';

  const goToNs = () => navigate(`/services/domaine/ns?domaine=${encodeURIComponent(domaineName)}`);
  const goToZoneDns = () => navigate(`/services/domaine/zone-dns?domaine=${encodeURIComponent(domaineName)}`);

  const [showContactModal, setShowContactModal] = useState(false);
  const [showDeleteRedirectionModal, setShowDeleteRedirectionModal] = useState(false);
  const [showEditRedirectionModal, setShowEditRedirectionModal] = useState(false);
  const [redirectionUrl, setRedirectionUrl] = useState('');
  const [redirectionUrlTouched, setRedirectionUrlTouched] = useState(false);
  const [contact, setContact] = useState<ContactFormValues>(INITIAL_CONTACT);

  const isValidUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleOpenEditRedirection = () => {
    setRedirectionUrlTouched(false);
    setShowEditRedirectionModal(true);
  };

  const handleSubmitEditRedirection = () => {
    setRedirectionUrlTouched(true);
    if (!isValidUrl(redirectionUrl)) return;
    setShowEditRedirectionModal(false);
  };

  const handleContactChange =
    (field: keyof ContactFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setContact(prev => ({...prev, [field]: e.target.value}));

  const contactModalBody = (
    <div className="d-flex flex-column gap-5">
      <Form.Group>
        <Form.Label className="fw-bold fs-6 mb-2">Type</Form.Label>
        <Form.Select value={contact.type} onChange={handleContactChange('type')}>
          <option value="Personne Morale">Personne Morale</option>
          <option value="Personne Physique">Personne Physique</option>
        </Form.Select>
      </Form.Group>

      <Form.Group>
        <Form.Label className="fw-bold fs-6 mb-2">Société</Form.Label>
        <Form.Control
          type="text"
          value={contact.societe}
          onChange={handleContactChange('societe')}
        />
      </Form.Group>

      <div className="row g-4">
        <div className="col-6">
          <Form.Group>
            <Form.Label className="fw-bold fs-6 mb-2">Nom</Form.Label>
            <Form.Control
              type="text"
              value={contact.nom}
              onChange={handleContactChange('nom')}
            />
          </Form.Group>
        </div>
        <div className="col-6">
          <Form.Group>
            <Form.Label className="fw-bold fs-6 mb-2">Prénom</Form.Label>
            <Form.Control
              type="text"
              value={contact.prenom}
              onChange={handleContactChange('prenom')}
            />
          </Form.Group>
        </div>
      </div>

      <Form.Group>
        <Form.Label className="fw-bold fs-6 mb-2">Email</Form.Label>
        <Form.Control
          type="email"
          value={contact.email}
          onChange={handleContactChange('email')}
        />
      </Form.Group>

      <Form.Group>
        <Form.Label className="fw-bold fs-6 mb-2">Téléphone</Form.Label>
        <InputGroup>
          <InputGroup.Text>+</InputGroup.Text>
          <Form.Control
            type="text"
            value={contact.telIndicatif}
            onChange={handleContactChange('telIndicatif')}
            style={{maxWidth: '60px'}}
          />
          <Form.Control
            type="text"
            value={contact.telNumero}
            onChange={handleContactChange('telNumero')}
          />
        </InputGroup>
      </Form.Group>

      <Form.Group>
        <Form.Label className="fw-bold fs-6 mb-2">Adresse</Form.Label>
        <Form.Control
          type="text"
          value={contact.adresse}
          onChange={handleContactChange('adresse')}
        />
      </Form.Group>

      <div className="row g-4">
        <div className="col-4">
          <Form.Group>
            <Form.Label className="fw-bold fs-6 mb-2">Code Postal</Form.Label>
            <Form.Control
              type="text"
              value={contact.codePostal}
              onChange={handleContactChange('codePostal')}
            />
          </Form.Group>
        </div>
        <div className="col-8">
          <Form.Group>
            <Form.Label className="fw-bold fs-6 mb-2">Ville</Form.Label>
            <Form.Control
              type="text"
              value={contact.ville}
              onChange={handleContactChange('ville')}
            />
          </Form.Group>
        </div>
      </div>

      <Form.Group>
        <Form.Label className="fw-bold fs-6 mb-2">Pays</Form.Label>
        <Form.Select value={contact.pays} onChange={handleContactChange('pays')}>
          <option value="FR">France</option>
          <option value="BE">Belgique</option>
          <option value="CH">Suisse</option>
          <option value="LU">Luxembourg</option>
          <option value="DE">Allemagne</option>
          <option value="ES">Espagne</option>
          <option value="IT">Italie</option>
        </Form.Select>
      </Form.Group>
    </div>
  );

  const RetourButton = (): JSX.Element => (
    <button
      type="button"
      className="btn btn-sm btn-outline btn-outline-primary btn-active-light-primary d-inline-flex align-items-center mb-5"
      onClick={() => navigate(-1)}
    >
      <i className="bi bi-arrow-left me-2" />
      Retour
    </button>
  );

  return (
    <>
      <PageTitle breadcrumbs={[]}>Détail du domaine</PageTitle>

      <RetourButton />

      <div className="row g-5">
        {/* Données du domaine + Redirection web */}
        <div className="col-lg-9">
          <div className="card mb-5">
            <div className="card-header border-0 pt-6">
              <div className="card-title">
                <Level3Title title="Données du domaine" />
              </div>
            </div>
            <div className="card-body pt-2">
              <div className="row">
                <InfoItem label="Domaine" value={domaineName} />
                <InfoItem label="Statut" value="Actif" />
                <InfoItem label="Créé le" value="07/06/2006" />
                <InfoItem label="Date d'expiration" value="02/06/2027" />

                <div className="col-md-8 mb-6">
                  <div className="text-muted fs-7 mb-1">Code d'Autorisation</div>
                  <div className="d-flex align-items-center gap-3">
                    <span className="fw-semibold fs-5 text-gray-800">***************</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary d-inline-flex align-items-center"
                      title="Déverrouiller et obtenir le code"
                    >
                      <i className="bi bi-eye" />
                    </button>
                  </div>
                </div>

                <InfoItem
                  label="Serveurs DNS"
                  colClass="col-12"
                  value="NS1 : ns1.sd-france.net     NS2 : ns2.sd-france.net"
                />
              </div>

              <div className="separator mb-5" />

              <div className="d-flex flex-wrap gap-3">
                <button
                  type="button"
                  className="btn btn-sm btn-primary d-inline-flex align-items-center"
                  onClick={goToNs}
                >
                  <i className="bi bi-pencil-square me-2" />
                  Modifier les NS
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-light-primary d-inline-flex align-items-center"
                  onClick={goToZoneDns}
                >
                  <i className="bi bi-at me-2" />
                  Gérer la zone DNS
                </button>
              </div>
            </div>
          </div>
          {/* Redirection web */}
          <div className="card">
            <div className="card-header border-0 pt-6">
              <div className="card-title">
                <Level3Title title="Redirection web" />
              </div>
            </div>
            <div className="card-body pt-2">
              <div className="notice d-flex bg-light-info rounded border-info border border-dashed p-6 mb-6">
                <i className="bi bi-info-circle fs-2tx text-info me-4" />
                <div className="d-flex flex-stack flex-grow-1">
                  <div className="fw-semibold text-gray-700">
                    Pour utiliser la fonctionnalité de redirection web, le domaine doit pointer vers
                    nos serveurs avec des entrées de type A vers les adresses IP{' '}
                    <strong>188.130.25.102</strong> et <strong>188.130.25.202</strong>
                  </div>
                </div>
              </div>

              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-primary d-inline-flex align-items-center"
                  onClick={handleOpenEditRedirection}
                >
                  <i className="bi bi-pencil-square me-2" />
                  Modifier redirection web
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-danger d-inline-flex align-items-center"
                  onClick={() => setShowDeleteRedirectionModal(true)}
                >
                  <i className="bi bi-trash me-2" />
                  Supprimer redirection web
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Contact propriétaire */}
        <div className="col-lg-3">
          <div className="card">
            <div className="card-header">
              <div className="card-title m-0">
                <h3 className="fs-3 fw-semibold">Contact propriétaire</h3>
              </div>
            </div>
            <div className="card-body px-9 py-6">
              <div className="d-flex flex-column gap-4">
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <span className="text-muted flex-shrink-0">Type</span>
                  <span className="fw-semibold text-gray-800 text-end">{contact.type}</span>
                </div>
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <span className="text-muted flex-shrink-0">Société</span>
                  <span className="fw-semibold text-gray-800 text-end">{contact.societe || '—'}</span>
                </div>
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <span className="text-muted flex-shrink-0">Nom</span>
                  <span className="fw-semibold text-gray-800 text-end">{contact.nom || '—'}</span>
                </div>
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <span className="text-muted flex-shrink-0">Prénom</span>
                  <span className="fw-semibold text-gray-800 text-end">{contact.prenom || '—'}</span>
                </div>
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <span className="text-muted flex-shrink-0">Email</span>
                  <span className="fw-semibold text-gray-800 text-end text-break">{contact.email}</span>
                </div>
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <span className="text-muted flex-shrink-0">Téléphone</span>
                  <span className="fw-semibold text-gray-800 text-end">+{contact.telIndicatif}.{contact.telNumero}</span>
                </div>
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <span className="text-muted flex-shrink-0">Adresse</span>
                  <span className="fw-semibold text-gray-800 text-end">{contact.adresse}</span>
                </div>
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <span className="text-muted flex-shrink-0">Code Postal</span>
                  <span className="fw-semibold text-gray-800 text-end">{contact.codePostal}</span>
                </div>
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <span className="text-muted flex-shrink-0">Ville</span>
                  <span className="fw-semibold text-gray-800 text-end">{contact.ville}</span>
                </div>
                <div className="d-flex justify-content-between align-items-start gap-3">
                  <span className="text-muted flex-shrink-0">Pays</span>
                  <span className="fw-semibold text-gray-800 text-end">{contact.pays}</span>
                </div>
              </div>

              <div className="separator my-6" />

              <button
                type="button"
                className="btn btn-sm btn-primary d-inline-flex align-items-center"
                onClick={() => setShowContactModal(true)}
              >
                <i className="bi bi-pencil-square me-2" />
                Modifier
              </button>
            </div>
          </div>
        </div>
      </div>

      <CustomModal
        show={showEditRedirectionModal}
        onHide={() => setShowEditRedirectionModal(false)}
        title="Modifier redirection web"
        body={
          <Form.Group>
            <Form.Label className="fw-bold fs-6 mb-2">
              URL de destination (ex: https://www.example.com)
            </Form.Label>
            <Form.Control
              type="url"
              placeholder="URL de destination (ex: https://www.example.com)"
              value={redirectionUrl}
              onChange={e => setRedirectionUrl(e.target.value)}
              onBlur={() => setRedirectionUrlTouched(true)}
              isInvalid={redirectionUrlTouched && !isValidUrl(redirectionUrl)}
            />
            <Form.Control.Feedback type="invalid">
              Il faut spécifier une URL de destination valide
            </Form.Control.Feedback>
          </Form.Group>
        }
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setShowEditRedirectionModal(false)}>
              <i className="bi bi-x me-1" />
              Annuler
            </Button>
            <Button variant="primary" size="sm" onClick={handleSubmitEditRedirection}>
              <i className="bi bi-pencil-square me-1" />
              Modifier
            </Button>
          </>
        }
      />

      <CustomModal
        show={showDeleteRedirectionModal}
        onHide={() => setShowDeleteRedirectionModal(false)}
        title="Supprimer la redirection web"
        body={
          <p className="text-gray-700">
            Êtes-vous sûr de vouloir supprimer la redirection web pour le domaine{' '}
            <strong>{domaineName}</strong> ? Cette action est irréversible.
          </p>
        }
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setShowDeleteRedirectionModal(false)}>
              <i className="bi bi-x me-1" />
              Annuler
            </Button>
            <Button variant="danger" size="sm" onClick={() => setShowDeleteRedirectionModal(false)}>
              <i className="bi bi-trash me-1" />
              Supprimer
            </Button>
          </>
        }
      />

      <CustomModal
        show={showContactModal}
        onHide={() => setShowContactModal(false)}
        title="Éditer le contact"
        size="lg"
        body={contactModalBody}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setShowContactModal(false)}>
              <i className="bi bi-x me-1" />
              Annuler
            </Button>
            <Button variant="primary" size="sm" onClick={() => setShowContactModal(false)}>
              <i className="bi bi-check me-1" />
              Valider
            </Button>
          </>
        }
      />
    </>
  );
};

export default DomaineWhoisPage;
